import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Download,
  FolderKanban,
  Layers,
  LayoutGrid,
  List as ListIcon,
  Lock,
  Plus,
  Search,
  Table as TableIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle, type ViewModeOption } from '@/components/ui/view-mode-toggle';
import { exportCsv } from '@/core/api';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { BASE as PROJECTS_BASE } from '../api';
import { projectPath } from '../paths';
import { cn } from '@/lib/utils';
import { CreateProjectDialog } from '../components/CreateProjectDialog';
import { AssigneeAvatars } from '../components/AssigneeAvatars';
import {
  PROJECT_STATUSES,
  PROJECT_VISIBILITIES,
  projectStatusLabel,
  projectStatusVariant,
} from '../constants';
import { useCanAdminProjects } from '../permissions';
import { useProjects } from '../queries';
import { useProjectsUi, type ProjectsViewMode } from '../store/viewPrefs';
import type { Project } from '../types';

/** Columns the backend lets us order by — keeps typos out of the URL. */
type ProjectOrderingColumn = 'name' | 'identifier' | 'status' | 'priority' | 'start_date' | 'end_date';

const VIEW_MODES: readonly ViewModeOption<ProjectsViewMode>[] = [
  { value: 'cards', label: 'Cards', icon: LayoutGrid },
  { value: 'table', label: 'Table', icon: TableIcon },
  { value: 'list', label: 'List', icon: ListIcon },
  { value: 'grouped', label: 'Grouped by status', icon: Layers },
];

export function ProjectsHomePage() {
  const canAdmin = useCanAdminProjects();
  const [createOpen, setCreateOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebouncedValue(searchInput.trim(), 300);

  const view = useProjectsUi((s) => s.view);
  const setView = useProjectsUi((s) => s.setView);
  const ordering = useProjectsUi((s) => s.ordering);
  const setOrdering = useProjectsUi((s) => s.setOrdering);
  const status = useProjectsUi((s) => s.status);
  const setStatus = useProjectsUi((s) => s.setStatus);
  const visibility = useProjectsUi((s) => s.visibility);
  const setVisibility = useProjectsUi((s) => s.setVisibility);
  const showArchived = useProjectsUi((s) => s.showArchived);
  const setShowArchived = useProjectsUi((s) => s.setShowArchived);

  const params = {
    search: search || undefined,
    status: status === 'all' ? undefined : status,
    visibility: visibility === 'all' ? undefined : visibility,
    is_archived: showArchived,
    ordering: ordering ?? 'name',
  };

  const { data, isPending, isError, error } = useProjects(params);
  const projects = (data?.results ?? []) as Project[];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
        {canAdmin ? (
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> New project
          </Button>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-56">
          <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search projects…"
            className="h-9 pl-8"
            aria-label="Search projects"
          />
        </div>

        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-36" aria-label="Filter by status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {PROJECT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={visibility} onValueChange={setVisibility}>
          <SelectTrigger className="h-9 w-32" aria-label="Filter by access"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All access</SelectItem>
            {PROJECT_VISIBILITIES.map((v) => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>

        <Button
          variant={showArchived ? 'secondary' : 'outline'}
          size="sm"
          className="h-9"
          onClick={() => setShowArchived(!showArchived)}
        >
          {showArchived ? 'Archived' : 'Active'}
        </Button>

        <div className="ml-auto flex items-center gap-2">
          <ViewModeToggle value={view} onChange={setView} modes={VIEW_MODES} ariaLabel="Projects view mode" />
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9"
            onClick={() => void exportCsv(PROJECTS_BASE, params)}
            aria-label="Export projects as CSV"
            title="Export projects as CSV"
          >
            <Download className="size-4" />
          </Button>
        </div>
      </div>

      {isPending ? (
        <ProjectsSkeleton view={view} />
      ) : isError ? (
        <div className="border-destructive bg-destructive/5 rounded-md border p-4">
          <p className="text-destructive text-sm">{error?.message}</p>
        </div>
      ) : projects.length === 0 ? (
        <div className="rounded-md border border-dashed p-10 text-center">
          <FolderKanban className="text-muted-foreground mx-auto size-8" />
          <p className="mt-3 font-medium">No projects found</p>
          <p className="text-muted-foreground text-sm">
            {search || status !== 'all' || visibility !== 'all' || showArchived
              ? 'Try clearing your search or filters.'
              : canAdmin
                ? 'Create your first project to get started.'
                : 'You have no project access yet.'}
          </p>
        </div>
      ) : view === 'table' ? (
        <ProjectsTable projects={projects} ordering={ordering} setOrdering={setOrdering} />
      ) : view === 'list' ? (
        <ProjectsList projects={projects} />
      ) : view === 'grouped' ? (
        <ProjectsGrouped projects={projects} />
      ) : (
        <ProjectsCards projects={projects} />
      )}

      <CreateProjectDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Views
// ---------------------------------------------------------------------------
function ProjectsCards({ projects }: { projects: Project[] }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {projects.map((p) => (
        <Link key={p.id} to={projectPath(p)}>
          <Card className="hover:border-primary/40 h-full transition-colors hover:shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className="text-lg">{p.icon || '📁'}</span>
                  <span className="truncate">{p.name}</span>
                </CardTitle>
                {p.visibility === 'private' ? (
                  <Lock className="text-muted-foreground size-3.5 shrink-0" />
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="flex items-center gap-2">
              {p.identifier ? (
                <Badge variant="outline" className="font-mono text-xs">{p.identifier}</Badge>
              ) : null}
              <Badge variant={projectStatusVariant(p.status)}>{projectStatusLabel(p.status)}</Badge>
              <LeadInfo project={p} className="ml-auto" />
            </CardContent>
          </Card>
        </Link>
      ))}
    </div>
  );
}

/** "Lead: Name" with the lead's avatar (HRM photo → initials). */
function LeadInfo({ project, className }: { project: Project; className?: string }) {
  if (!project.lead_name) return null;
  return (
    <span className={cn('text-muted-foreground flex min-w-0 items-center gap-1.5 text-xs', className)}>
      <span className="truncate">Lead: {project.lead_name}</span>
      <AssigneeAvatars
        assignees={[{ id: project.lead ?? 0, name: project.lead_name, photo: project.lead_photo }]}
        max={1}
      />
    </span>
  );
}

function ProjectsList({ projects }: { projects: Project[] }) {
  return (
    <ul className="divide-y rounded-md border">
      {projects.map((p) => (
        <li key={p.id}>
          <Link
            to={projectPath(p)}
            className="hover:bg-muted/40 flex items-center gap-3 px-3 py-2.5"
          >
            <span className="text-lg">{p.icon || '📁'}</span>
            <span className="flex-1 truncate text-sm font-medium">{p.name}</span>
            {p.visibility === 'private' ? <Lock className="text-muted-foreground size-3.5" /> : null}
            {p.identifier ? (
              <Badge variant="outline" className="font-mono text-xs">{p.identifier}</Badge>
            ) : null}
            <Badge variant={projectStatusVariant(p.status)}>{projectStatusLabel(p.status)}</Badge>
            <LeadInfo project={p} className="max-w-48 justify-end" />
          </Link>
        </li>
      ))}
    </ul>
  );
}

function ProjectsTable({
  projects,
  ordering,
  setOrdering,
}: {
  projects: Project[];
  ordering: string | undefined;
  setOrdering: (next: string | undefined) => void;
}) {
  const navigate = useNavigate();
  const head = (column: ProjectOrderingColumn, label: string) => (
    <SortableTableHead column={column} value={ordering} onChange={setOrdering}>
      {label}
    </SortableTableHead>
  );
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            {head('name', 'Name')}
            {head('identifier', 'ID')}
            {head('status', 'Status')}
            {head('priority', 'Priority')}
            {head('start_date', 'Start')}
            {head('end_date', 'Due')}
          </TableRow>
        </TableHeader>
        <TableBody>
          {projects.map((p) => (
            <TableRow
              key={p.id}
              className="cursor-pointer"
              onClick={() => navigate(projectPath(p))}
            >
              <TableCell className="font-medium">
                <span className="mr-1.5">{p.icon || '📁'}</span>
                {p.name}
                {p.visibility === 'private' ? (
                  <Lock className="text-muted-foreground ml-1.5 inline size-3.5" />
                ) : null}
              </TableCell>
              <TableCell className="font-mono text-xs">{p.identifier || '—'}</TableCell>
              <TableCell>
                <Badge variant={projectStatusVariant(p.status)}>{projectStatusLabel(p.status)}</Badge>
              </TableCell>
              <TableCell className="capitalize">{p.priority}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{p.start_date ?? '—'}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{p.end_date ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ProjectsGrouped({ projects }: { projects: Project[] }) {
  return (
    <div className="space-y-6">
      {PROJECT_STATUSES.map((s) => {
        const group = projects.filter((p) => p.status === s.value);
        if (group.length === 0) return null;
        return (
          <section key={s.value} className="space-y-3">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              {s.label}
              <span className="text-muted-foreground font-normal">{group.length}</span>
            </h2>
            <ProjectsCards projects={group} />
          </section>
        );
      })}
    </div>
  );
}

function ProjectsSkeleton({ view }: { view: ProjectsViewMode }) {
  if (view === 'table' || view === 'list') {
    return <Skeleton className="h-64 w-full" />;
  }
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-32 w-full" />
      ))}
    </div>
  );
}
